import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { QueryState } from '@/components/QueryState'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { explainOrderPrice } from '@/features/assistant/api'
import { ExplainPrice } from '@/features/assistant/AssistantPanel'
import { useAuth } from '@/features/auth/store'
import { createInvoice } from '@/features/invoices/api'
import { ShipmentCard } from '@/features/orders/ShipmentCard'
import { StatusBadge } from '@/features/orders/StatusBadge'
import {
  approveOrder,
  bookShipment,
  cancelShipment,
  shippingDocument,
  cancelOrder,
  formatMoney,
  fulfilOrder,
  getOrder,
  listLocations,
  refundOrder,
  reopenOrder,
  submitOrder,
  updateOrder,
  type Order,
} from '@/features/orders/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-8 rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

/**
 * The delivery charge, editable on an internal draft. A storefront order's delivery was
 * quoted at checkout and paid by card, so it is shown but never edited here.
 */
function DeliveryRow({ order, canEdit }: { order: Order; canEdit: boolean }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(order.shipping_amount)
  const editable = canEdit && order.status === 'draft' && order.channel === 'internal'
  const valid = /^\d+(\.\d{1,2})?$/.test(value.trim())
  const changed = valid && Number(value) !== Number(order.shipping_amount)

  const save = useMutation({
    mutationFn: () => updateOrder(order.id, { shipping_amount: Number(value).toFixed(2) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['order', order.id] })
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast.success('Delivery charge updated.')
    },
    onError: (error: unknown) =>
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing was changed.',
      ),
  })

  if (!editable) {
    return (
      <div className="flex justify-between">
        <dt className="text-slate-500 dark:text-slate-400">Delivery</dt>
        <dd className="tabular-nums">{formatMoney(order.shipping_amount)}</dd>
      </div>
    )
  }
  return (
    <div className="flex items-center justify-between gap-2">
      <dt>
        <label htmlFor="delivery-charge" className="text-slate-500 dark:text-slate-400">
          Delivery
        </label>
      </dt>
      <dd className="flex items-center gap-1">
        <Input
          id="delivery-charge"
          inputMode="decimal"
          className="h-8 w-24 text-right tabular-nums"
          value={value}
          aria-invalid={!valid}
          onChange={(event) => setValue(event.target.value)}
        />
        {changed && (
          <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        )}
      </dd>
    </div>
  )
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-900 dark:text-slate-100">{children}</dd>
    </div>
  )
}

/** Both a countdown and a warning: held stock that nobody ships goes back on the shelf. */
function ReservationNotice({ order }: { order: Order }) {
  if (order.status !== 'approved' || !order.reservation_expires_at) return null

  const expiresAt = new Date(order.reservation_expires_at)
  const overdue = expiresAt.getTime() < Date.now()

  return (
    <p
      className={
        overdue
          ? 'rounded-md bg-danger/10 px-3 py-2 text-sm text-danger'
          : 'rounded-md bg-warning/10 px-3 py-2 text-sm text-warning'
      }
    >
      {overdue
        ? `Stock was held until ${expiresAt.toLocaleString()} and is being released.`
        : `Stock is held until ${expiresAt.toLocaleString()}. Ship before then or it goes back into available stock.`}
    </p>
  )
}

export function OrderDetail() {
  const { orderId = '' } = useParams()
  const [cancelling, setCancelling] = useState(false)
  const [reason, setReason] = useState('')
  const can = useAuth((s) => s.can)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    data: order,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId),
    retry: false,
  })

  const { data: locationsPage } = useQuery({
    queryKey: ['locations'],
    queryFn: () => listLocations(),
  })
  const locationName = locationsPage?.data.find((l) => l.id === order?.location_id)?.name

  /**
   * One mutation for every transition.
   *
   * They differ only in which call they make and what to say afterwards, and the failure
   * handling is the part that matters: a refused approval is a business answer -- over the
   * credit limit, not enough stock -- so the server's message is shown verbatim rather
   * than replaced with something vaguer.
   */
  const act = useMutation({
    mutationFn: ({ run }: { run: () => Promise<Order>; message: string }) => run(),
    onSuccess: (order, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      setCancelling(false)
      setReason('')
      // The courier refusing again is recorded on the order, not raised as an error.
      if (order.shipment_status?.startsWith('Booking failed')) toast.error(order.shipment_status)
      else toast.success(variables.message)
    },
    onError: (error: unknown) => {
      void queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  /**
   * Bill what has shipped.
   *
   * Sending no lines asks the API for everything on the order that has not been invoiced
   * yet -- the whole order the first time, the remainder after a part invoice. It lands as
   * a draft, so the screen it opens is where somebody checks it before it goes out.
   */
  const raise = useMutation({
    mutationFn: () => createInvoice({ order_id: orderId }),
    onSuccess: (invoice) => {
      void queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      void queryClient.invalidateQueries({ queryKey: ['invoices'] })
      toast.success(`Draft invoice ${invoice.invoice_number} raised.`)
      navigate(`/invoices/${invoice.id}`)
    },
    onError: (error: unknown) =>
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. No invoice was raised.',
      ),
  })

  /**
   * Where it ships from. Draft-only, same as every other header field -- a location under
   * an order that has already gone out for approval would mean the stock reservation and
   * the "ships from" answer could disagree.
   */
  const changeLocation = useMutation({
    mutationFn: (locationId: string) => updateOrder(orderId, { location_id: locationId || null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['order', orderId] })
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      toast.success('Ships from updated.')
    },
    onError: (error: unknown) =>
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing was changed.',
      ),
  })

  // Loading, forbidden and failed all render as themselves; everything below assumes an
  // order that actually arrived, rather than threading `order?.` through two hundred lines.
  if (isLoading || error || !order) {
    return (
      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={!order}
        emptyMessage="That order does not exist, or is not yours to see."
      >
        {null}
      </QueryState>
    )
  }

  const canEdit = can('invoices', 'update')
  /** Open Shiprocket's PDF in a new tab. The tab opens on the click, before the link is
   * fetched, so the browser does not take it for a pop-up. */
  const print = async (kind: 'label' | 'manifest') => {
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    try {
      const { url } = await shippingDocument(order.id, kind)
      if (tab) tab.location.href = url
      else window.location.href = url
    } catch (error) {
      tab?.close()
      toast.error(error instanceof ApiError ? error.message : 'Could not reach the server.')
    }
  }
  const canRaise = can('invoices', 'create')
  // A storefront order paid at checkout cannot simply be cancelled: the API refuses with
  // ORDER_PAID, and the money has to go back to the card in the same step.
  const paid = order.payment_status === 'paid'
  // A storefront order is the shopper's basket: its payment submits it, nothing on it is
  // edited here, and until the card payment lands it goes no further (there is no COD).
  const internal = order.channel === 'internal'
  const awaitingPayment = !internal && !paid && order.status !== 'cancelled'

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/orders" className="text-sm text-slate-500 hover:underline dark:text-slate-400">
            ← All orders
          </Link>
          <h1 className="mt-1 flex items-center gap-3 text-xl font-semibold tracking-tight">
            {order.order_number}
            <StatusBadge status={order.status} />
          </h1>
        </div>

        <div className="flex flex-wrap gap-2">
          {order.status === 'draft' && internal && canRaise && (
            <Button
              loading={act.isPending}
              onClick={() =>
                act.mutate({
                  run: () => submitOrder(order.id),
                  message: 'Sent for approval.',
                })
              }
            >
              Send for approval
            </Button>
          )}
          {order.status === 'submitted' && canEdit && (
            <>
              {internal && (
                <Button
                  variant="secondary"
                  loading={act.isPending}
                  onClick={() =>
                    act.mutate({
                      run: () => reopenOrder(order.id),
                      message: 'Back in draft.',
                    })
                  }
                >
                  Back to draft
                </Button>
              )}
              {!awaitingPayment && (
                <Button
                  loading={act.isPending}
                  onClick={() =>
                    act.mutate({
                      run: () => approveOrder(order.id),
                      message: 'Approved. Stock is held.',
                    })
                  }
                >
                  Approve
                </Button>
              )}
            </>
          )}
          {order.status === 'approved' && !awaitingPayment && canEdit && (
            <Button
              loading={act.isPending}
              onClick={() =>
                act.mutate({
                  run: () => fulfilOrder(order.id),
                  message: 'Shipped. Stock has left the shelf.',
                })
              }
            >
              Mark as shipped
            </Button>
          )}
          {['fulfilled', 'invoiced'].includes(order.status) &&
            canEdit &&
            order.channel === 'marketplace' &&
            order.shipping_address &&
            // Never booked, refused or called off; mirrors `events.needs_courier`.
            (!order.shipment_status ||
              order.shipment_status.startsWith('Booking failed') ||
              order.shipment_status === 'Courier cancelled') && (
              <Button
                variant="secondary"
                loading={act.isPending}
                onClick={() =>
                  act.mutate({
                    run: () => bookShipment(order.id),
                    message: 'Courier booked.',
                  })
                }
              >
                {order.shipment_status ? 'Book courier again' : 'Book courier'}
              </Button>
            )}
          {order.awb_code && (
            <>
              <Button variant="secondary" onClick={() => void print('label')}>
                Print label
              </Button>
              <Button variant="secondary" onClick={() => void print('manifest')}>
                Print manifest
              </Button>
            </>
          )}
          {order.shipment_ref && canEdit && (
            <Button
              variant="danger"
              loading={act.isPending}
              onClick={() =>
                window.confirm('Call this parcel off with the courier?') &&
                act.mutate({
                  run: () => cancelShipment(order.id),
                  message: 'Courier cancelled. The order can be booked again.',
                })
              }
            >
              Cancel courier
            </Button>
          )}
          {order.status === 'fulfilled' && canRaise && (
            <Button loading={raise.isPending} onClick={() => raise.mutate()}>
              Raise invoice
            </Button>
          )}
          {['draft', 'submitted', 'approved'].includes(order.status) && canEdit && (
            <Button variant="danger" onClick={() => setCancelling(true)}>
              {paid ? 'Cancel and refund' : 'Cancel order'}
            </Button>
          )}
        </div>
      </header>

      <ReservationNotice order={order} />
      {awaitingPayment && (
        <p className="rounded-md bg-warning/10 px-3 py-2 text-sm text-warning">
          Awaiting the shopper’s card payment. The order cannot be approved or shipped until it is
          paid.
        </p>
      )}

      <Card>
        <CardHeader title="Order" />
        <CardBody>
          <dl className="grid grid-cols-2 gap-4 tablet:grid-cols-4">
            <Detail label="Location">
              {order.status === 'draft' && internal && canEdit ? (
                <select
                  className={SELECT_CLASS}
                  value={order.location_id ?? ''}
                  disabled={changeLocation.isPending}
                  onChange={(event) => changeLocation.mutate(event.target.value)}
                >
                  <option value="">Choose a location…</option>
                  {(locationsPage?.data ?? []).map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              ) : (
                (locationName ?? (order.location_id ? '—' : 'Not set'))
              )}
            </Detail>
            <Detail label="Raised">{new Date(order.order_date).toLocaleString()}</Detail>
            <Detail label="Delivery date">
              {order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : 'Not set'}
            </Detail>
            <Detail label="Approved">
              {order.approved_at ? new Date(order.approved_at).toLocaleString() : '—'}
            </Detail>
            <Detail label="Shipped">
              {order.fulfilled_at ? new Date(order.fulfilled_at).toLocaleString() : '—'}
            </Detail>
          </dl>
          {order.notes && (
            <p className="mt-4 whitespace-pre-line text-sm text-slate-600 dark:text-slate-300">
              {order.notes}
            </p>
          )}
          {order.cancel_reason && (
            <p className="mt-4 text-sm text-danger">Cancelled: {order.cancel_reason}</p>
          )}
          {order.channel === 'marketplace' && order.payment_status !== 'unpaid' && (
            <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">
              {order.payment_status === 'refunded'
                ? `Refunded to the customer's card${order.refunded_at ? ` on ${new Date(order.refunded_at).toLocaleString()}` : ''}.`
                : `Paid by card at checkout${order.paid_at ? ` on ${new Date(order.paid_at).toLocaleString()}` : ''}.`}
            </p>
          )}
        </CardBody>
      </Card>

      {order.shipping_address && (
        <Card>
          <CardHeader title="Shipping address" />
          <CardBody className="text-sm text-slate-700 dark:text-slate-300">
            <p className="font-medium text-slate-900 dark:text-slate-100">
              {order.shipping_address.name}
            </p>
            <p>{order.shipping_address.line1}</p>
            {order.shipping_address.line2 && <p>{order.shipping_address.line2}</p>}
            <p>
              {order.shipping_address.city}
              {order.shipping_address.state ? `, ${order.shipping_address.state}` : ''}{' '}
              {order.shipping_address.postal_code}
            </p>
            <p>{order.shipping_address.country}</p>
            {order.shipping_address.phone && (
              <p className="mt-1 text-slate-500 dark:text-slate-400">
                {order.shipping_address.dial_code ?? ''} {order.shipping_address.phone}
              </p>
            )}
          </CardBody>
        </Card>
      )}

      <ShipmentCard shipment={order} />

      <Card>
        <CardHeader title="Lines" description="Prices as they were when the order was placed." />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3">Product</th>
                <th className="px-5 py-3 text-right">Qty</th>
                <th className="px-5 py-3 text-right">Held</th>
                <th className="px-5 py-3 text-right">Unit price</th>
                <th className="px-5 py-3 text-right">Discount</th>
                <th className="px-5 py-3 text-right">Tax</th>
                <th className="px-5 py-3 text-right">Line total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {order.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5 font-medium">
                      {item.product_data.name ?? 'Product'}
                      {item.product_data.price_anomaly && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-normal text-amber-800 dark:bg-amber-500/10 dark:text-amber-400"
                          title={
                            item.product_data.price_anomaly_reason ??
                            'Price outside the expected range'
                          }
                        >
                          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                          Price flagged
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {item.product_data.code}
                    </div>
                    {item.product_data.price_anomaly && (
                      <ExplainPrice ask={() => explainOrderPrice(order.id, item.id)} />
                    )}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">{item.quantity}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-500 dark:text-slate-400">
                    {item.reserved_quantity || '—'}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {formatMoney(item.unit_price)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {formatMoney(item.discount_amount)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {formatMoney(item.tax_amount)}
                  </td>
                  <td className="px-5 py-3 text-right font-medium tabular-nums">
                    {formatMoney(item.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end border-t border-slate-200 px-5 py-4 dark:border-slate-800">
          <dl className="w-64 space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Subtotal</dt>
              <dd className="tabular-nums">{formatMoney(order.subtotal)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Order discount</dt>
              <dd className="tabular-nums">−{formatMoney(order.discount_amount)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Tax</dt>
              <dd className="tabular-nums">{formatMoney(order.tax_amount)}</dd>
            </div>
            <DeliveryRow order={order} canEdit={canEdit} />
            <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold dark:border-slate-800">
              <dt>Total</dt>
              <dd className="tabular-nums">{formatMoney(order.amount_due)}</dd>
            </div>
          </dl>
        </div>
      </Card>

      <Modal
        open={cancelling}
        onClose={() => setCancelling(false)}
        title={paid ? 'Cancel and refund this order?' : 'Cancel this order?'}
        description={
          paid
            ? `The customer paid ${formatMoney(order.amount_due)} by card at checkout. All of it goes back to that card, and any stock held for the order is released. This cannot be undone.`
            : 'Any stock held for it goes back into available stock. This cannot be undone.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelling(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={act.isPending}
              onClick={() =>
                act.mutate(
                  paid
                    ? {
                        run: () => refundOrder(order.id, reason || undefined),
                        message: 'Order cancelled and refunded to the card.',
                      }
                    : {
                        run: () => cancelOrder(order.id, reason || undefined),
                        message: 'Order cancelled. Stock released.',
                      },
                )
              }
            >
              {paid ? 'Cancel and refund' : 'Cancel order'}
            </Button>
          </>
        }
      >
        <label
          className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          htmlFor="cancel-reason"
        >
          Reason (optional)
        </label>
        <Input
          id="cancel-reason"
          className="mt-1.5"
          value={reason}
          maxLength={500}
          placeholder="Customer changed their mind"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>

      {order.status === 'cancelled' && canRaise && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          <button type="button" className="hover:underline" onClick={() => navigate('/orders/new')}>
            Raise a replacement order
          </button>
        </p>
      )}
    </div>
  )
}
